import { useMemo, useRef, useState, type ChangeEvent } from 'react';
import { useParams } from 'react-router-dom';
import { BottomSheet } from '../components/BottomSheet';
import { Button } from '../components/Button';
import { EmptyState, ErrorState, Skeleton } from '../components/States';
import { useAuth } from '../hooks/useAuth';
import { useCountry, useHistory, useWeapons, useWikiMutations } from '../hooks/useWiki';
import type { CountryRow, WeaponRow, WikiImage } from '../types/db';
import { makeId } from '../lib/utils';
import { MapView } from '../components/MapView';
const categories = [
  'Ballistic Missiles',
  'Cruise Missiles',
  'UAV / Drones',
  'Air Defense',
  'Aircraft',
  'Naval',
  'Ground',
  'Mines',
];
const specOrder = [
  'range',
  'payload',
  'speed',
  'guidance',
  'cep',
  'stock',
  'used',
  'destroyed',
  'first_seen',
  'status',
  'combat_use',
];
const specLabels: Record<string, string> = {
  range: 'Range',
  payload: 'Payload',
  speed: 'Speed',
  endurance: 'Endurance',
  ceiling: 'Ceiling',
  altitude: 'Altitude',
  crew: 'Crew',
  displacement: 'Displacement',
  weight: 'Weight',
  activation: 'Activation',
  emplacement: 'Emplacement',
  'max speed': 'Max speed',
  stock: 'Est. stock',
  used: 'Est. used',
  destroyed: 'Est. destroyed',
  guidance: 'Guidance',
  cep: 'CEP',
  first_seen: 'First seen',
  status: 'Status',
  combat_use: 'Combat use',
};
const statLabels: Record<string, string> = {
  active_personnel: 'Active personnel',
  reserve: 'Reserve',
  ballistic_stock: 'Ballistic stock',
  uav_fleet: 'UAV fleet',
  combat_aircraft: 'Combat aircraft',
  naval_major: 'Major naval assets',
  defence_budget_est: 'Defense budget',
  missile_production: 'Missile production',
};
const humanize = (key: string) =>
  key.replaceAll('_', ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
const orderedSpecs = (specs: Record<string, string>) =>
  Object.entries(specs).sort(([a], [b]) => {
    const ai = specOrder.indexOf(a);
    const bi = specOrder.indexOf(b);
    if (ai === -1 && bi === -1) return 0;
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });
const UNIVERSAL_SPECS = ['stock', 'used', 'destroyed', 'status', 'first_seen'];
// Felder-Schema je Kategorie: nur relevante Felder erscheinen (Landmine: keine Speed)
const CATEGORY_SPECS: Record<string, string[]> = {
  'Ballistic Missiles': ['range', 'payload', 'speed', 'guidance', 'cep'],
  'Cruise Missiles': ['range', 'payload', 'speed', 'guidance', 'cep'],
  'UAV / Drones': ['range', 'endurance', 'payload', 'speed', 'ceiling', 'guidance'],
  'Air Defense': ['range', 'altitude', 'speed', 'guidance'],
  Aircraft: ['max speed', 'payload', 'range', 'ceiling', 'crew'],
  Naval: ['displacement', 'speed', 'crew', 'payload', 'range'],
  Ground: ['weight', 'payload', 'speed', 'crew', 'range'],
  Mines: ['payload', 'activation', 'emplacement'],
};
const cardSpecs = (category: string, specs: Record<string, string>) => {
  const fields = [...(CATEGORY_SPECS[category] ?? []), ...UNIVERSAL_SPECS];
  const entries = Object.entries(specs).filter(([k]) => k !== 'combat_use' && fields.includes(k));
  const known = entries
    .filter(([k]) => fields.includes(k))
    .sort(([a], [b]) => fields.indexOf(a) - fields.indexOf(b));
  const extra = entries.filter(([k]) => !fields.includes(k));
  return [...known, ...extra];
};
const editorSpecs = (category: string, specs: Record<string, string>) => {
  const fields = [...(CATEGORY_SPECS[category] ?? []), ...UNIVERSAL_SPECS];
  return orderedSpecs(specs).sort(([a], [b]) => {
    const ai = fields.indexOf(a);
    const bi = fields.indexOf(b);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });
};

function CountryEditor({
  country,
  onClose,
  onSave,
}: {
  country: CountryRow;
  onClose: () => void;
  onSave: (
    country: Pick<
      CountryRow,
      | 'id'
      | 'summary'
      | 'government'
      | 'geography'
      | 'doctrine'
      | 'industry'
      | 'history'
      | 'alliances'
      | 'forces'
    >,
  ) => Promise<void>;
}) {
  const [draft, setDraft] = useState(country);
  const [forcesJson, setForcesJson] = useState(JSON.stringify(country.forces ?? {}, null, 2));
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  return (
    <BottomSheet
      title="Edit country"
      subtitle="Community-maintained country profile"
      onClose={onClose}
    >
      <form
        className="wiki-form"
        onSubmit={async (event) => {
          event.preventDefault();
          setBusy(true);
          try {
            const forces = JSON.parse(forcesJson) as CountryRow['forces'];
            if (!forces || Array.isArray(forces) || typeof forces !== 'object') {
              throw new Error('Forces must be a JSON object.');
            }
            await onSave({ ...draft, forces });
            onClose();
          } catch (error) {
            setMessage(error instanceof Error ? error.message : 'Save failed.');
          } finally {
            setBusy(false);
          }
        }}
      >
        <label>
          Summary
          <textarea
            rows={5}
            value={draft.summary}
            onChange={(e) => setDraft({ ...draft, summary: e.target.value })}
          />
        </label>
        <label>
          Government
          <textarea
            rows={6}
            value={draft.government ?? ''}
            onChange={(e) => setDraft({ ...draft, government: e.target.value })}
          />
        </label>
        <label>
          Geography
          <textarea
            rows={6}
            value={draft.geography ?? ''}
            onChange={(e) => setDraft({ ...draft, geography: e.target.value })}
          />
        </label>
        <label>
          Doctrine
          <textarea
            rows={6}
            value={draft.doctrine ?? ''}
            onChange={(e) => setDraft({ ...draft, doctrine: e.target.value })}
          />
        </label>
        <label>
          Armed forces <small>JSON object with one entry per service</small>
          <textarea
            rows={14}
            value={forcesJson}
            onChange={(e) => setForcesJson(e.target.value)}
            spellCheck={false}
          />
        </label>
        <label>
          Defense industry
          <textarea
            rows={6}
            value={draft.industry ?? ''}
            onChange={(e) => setDraft({ ...draft, industry: e.target.value })}
          />
        </label>
        <label>
          History <small>One timeline entry per line</small>
          <textarea
            rows={7}
            value={(draft.history ?? []).join('\n')}
            onChange={(e) =>
              setDraft({
                ...draft,
                history: e.target.value
                  .split('\n')
                  .map((x) => x.trim())
                  .filter(Boolean),
              })
            }
          />
        </label>
        <label>
          Alliances &amp; partners <small>Comma-separated</small>
          <textarea
            rows={3}
            value={(draft.alliances ?? []).join(', ')}
            onChange={(e) =>
              setDraft({
                ...draft,
                alliances: e.target.value
                  .split(',')
                  .map((x) => x.trim())
                  .filter(Boolean),
              })
            }
          />
        </label>
        {message && <p role="status">{message}</p>}
        <div className="form-actions">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={busy}>
            Save country
          </Button>
        </div>
      </form>
    </BottomSheet>
  );
}
function WeaponEditor({
  weapon,
  onClose,
  onSave,
  upload,
}: {
  weapon: WeaponRow;
  onClose: () => void;
  onSave: (w: WeaponRow) => Promise<void>;
  upload: (f: File, c: string) => Promise<WikiImage>;
}) {
  const [draft, setDraft] = useState(weapon);
  const [caption, setCaption] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const changeSpec = (old: string, key: string, value: string) =>
    setDraft((x) => {
      const specs = { ...x.specs };
      delete specs[old];
      specs[key] = value;
      return { ...x, specs };
    });
  const image = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const item = await upload(file, caption);
      setDraft((x) => ({ ...x, images: [...x.images, item] }));
      setMessage('Image ready to save.');
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Upload failed.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <BottomSheet
      title={weapon.version ? 'Edit system' : 'Add system'}
      subtitle={weapon.category}
      onClose={onClose}
    >
      <form
        className="wiki-form"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          try {
            await onSave(draft);
            onClose();
          } catch (err) {
            setMessage(err instanceof Error ? err.message : 'Save failed.');
          } finally {
            setBusy(false);
          }
        }}
      >
        <label>
          Name
          <input
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            required
          />
        </label>
        <label>
          Type
          <input
            value={draft.type}
            onChange={(e) => setDraft({ ...draft, type: e.target.value })}
          />
        </label>
        <label>
          Description
          <textarea
            aria-label="Description"
            value={draft.description}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            rows={5}
          />
        </label>
        <fieldset>
          <legend>Specifications</legend>
          {editorSpecs(draft.category, draft.specs).map(([key, value]) => (
            <div className="spec-edit" key={key}>
              <input
                aria-label="Spec name"
                value={key}
                onChange={(e) => changeSpec(key, e.target.value, value)}
              />
              <input
                aria-label={`${key} value`}
                value={value}
                onChange={(e) => changeSpec(key, key, e.target.value)}
              />
            </div>
          ))}
          <Button
            type="button"
            variant="ghost"
            onClick={() => setDraft({ ...draft, specs: { ...draft.specs, 'New spec': '' } })}
          >
            + Spec
          </Button>
        </fieldset>
        <label>
          Image caption
          <input value={caption} onChange={(e) => setCaption(e.target.value)} />
        </label>
        <label className="upload-button">
          {busy ? 'Uploading…' : 'Upload image'}
          <input
            aria-label="Upload image"
            type="file"
            accept="image/*"
            onChange={(e) => void image(e)}
          />
        </label>
        {draft.images.length > 0 && (
          <div className="upload-previews">
            {draft.images.map((x, i) => (
              <figure key={`${x.url}-${i}`}>
                <img src={x.url} alt={x.caption || draft.name} />
                <figcaption>{x.caption || 'New image'}</figcaption>
              </figure>
            ))}
          </div>
        )}
        {message && <p role="status">{message}</p>}
        <div className="form-actions">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={busy}>
            Save changes
          </Button>
        </div>
      </form>
    </BottomSheet>
  );
}
function History({ id, onClose }: { id: string; onClose: () => void }) {
  const q = useHistory(id);
  return (
    <BottomSheet title="Edit history" subtitle="Permanent version record" onClose={onClose}>
      {q.isLoading ? (
        <Skeleton rows={2} />
      ) : q.data?.length ? (
        <ol className="history-list">
          {q.data.map((x) => (
            <li key={x.id}>
              <b>{x.editor ?? 'Unknown editor'}</b>
              <time>{new Date(x.created_at).toLocaleString()}</time>
              <span>Version fields updated</span>
            </li>
          ))}
        </ol>
      ) : (
        <EmptyState title="No history yet" message="The first edit will appear here." />
      )}
    </BottomSheet>
  );
}
export function CountryPage() {
  const { id = 'iran' } = useParams();
  const { user } = useAuth();
  const country = useCountry(id);
  const weapons = useWeapons(id);
  const actions = useWikiMutations(user?.id ?? '');
  const [editingCountry, setEditingCountry] = useState(false);
  const [editing, setEditing] = useState<WeaponRow | null>(null);
  const [history, setHistory] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<{ image: WikiImage; name: string } | null>(null);
  const [activeCat, setActiveCat] = useState('All');
  const [query, setQuery] = useState('');
  const [selectedWeapon, setSelectedWeapon] = useState<WeaponRow | null>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const countryEvents = useMemo(() => {
    const g = (window as any).ACLED;
    const name = country.data?.name;
    if (!g || !name) return [];
    const all = [...(g.posts ?? []), ...(g.more ?? [])];
    return all.filter((p) => p.country === name);
  }, [country.data]);
  const catOrder = useMemo(() => {
    const seen = new Set((weapons.data ?? []).map((w) => w.category));
    return categories.concat([...seen].filter((c) => !categories.includes(c)));
  }, [weapons.data]);
  const grouped = useMemo(
    () =>
      Object.fromEntries(
        catOrder.map((c) => [c, (weapons.data ?? []).filter((w) => w.category === c)]),
      ),
    [catOrder, weapons.data],
  );
  const counts = useMemo(() => {
    const m: Record<string, number> = {};
    (weapons.data ?? []).forEach((w) => { m[w.category] = (m[w.category] ?? 0) + 1; });
    return m;
  }, [weapons.data]);
  const q = query.trim().toLowerCase();
  const matches = (w: WeaponRow) =>
    !q || [w.name, w.type, w.description].some((t) => t.toLowerCase().includes(q));
  if (country.isLoading || weapons.isLoading)
    return (
      <div className="page">
        <Skeleton rows={5} />
      </div>
    );
  if (country.error || weapons.error || !country.data)
    return (
      <div className="page">
        <ErrorState
          message="Country analysis could not be loaded."
          retry={() => {
            void country.refetch();
            void weapons.refetch();
          }}
        />
      </div>
    );
  const c = country.data;
  const blank = (category: string): WeaponRow => ({
    id: makeId(),
    country: c.name,
    category,
    name: '',
    type: '',
    description: '',
    specs: Object.fromEntries((CATEGORY_SPECS[category] ?? ['range', 'status']).map((k) => [k, ''])),
    images: [],
    created_by: user?.id ?? null,
    updated_by: null,
    version: 0,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  });
  return (
    <div className="page country-page">
      <header className="country-hero">
        <span className="country-flag">{c.flag}</span>
        <div>
          <span className="eyebrow">Country analysis</span>
          <h1>{c.name}</h1>
          <p>Islamic Republic of Iran</p>
          <small>Updated {new Date(c.updated_at).toLocaleDateString()}</small>
        </div>
        {user ? (
          <Button variant="secondary" onClick={() => setEditingCountry(true)}>
            Edit country
          </Button>
        ) : (
          <span className="signin-hint">Sign in to edit</span>
        )}
      </header>
      <div className="country-layout">
      <div className="country-main">
      <section className="wiki-summary">
        <div className="wiki-section-head">
          <h2>Overview</h2>
          <span>Community maintained</span>
        </div>
        <p>{c.summary}</p>
      </section>
      <div className="country-wiki-grid country-wiki-grid--overview">
        <section className="country-wiki-section" aria-labelledby="government-heading">
          <div className="wiki-section-head">
            <h2 id="government-heading">Government</h2>
            <span>Political system</span>
          </div>
          <p>{c.government || 'No government analysis has been added yet.'}</p>
        </section>
        <section className="country-wiki-section" aria-labelledby="geography-heading">
          <div className="wiki-section-head">
            <h2 id="geography-heading">Geography</h2>
            <span>Strategic terrain</span>
          </div>
          <p>{c.geography || 'No geography analysis has been added yet.'}</p>
        </section>
      </div>
      <section className="country-stats">
        {Object.entries(c.stats)
          .filter(([, value]) => value)
          .map(([key, value]) => (
            <article key={key}>
              <span>{statLabels[key] ?? humanize(key)}</span>
              <strong>{value}</strong>
            </article>
          ))}
      </section>
      <div className="country-wiki-grid">
        <section className="country-wiki-section" aria-labelledby="doctrine-heading">
          <div className="wiki-section-head">
            <h2 id="doctrine-heading">Doctrine</h2>
            <span>Strategic posture</span>
          </div>
          <p>{c.doctrine || 'No doctrine analysis has been added yet.'}</p>
        </section>
        <section className="country-wiki-section" aria-labelledby="industry-heading">
          <div className="wiki-section-head">
            <h2 id="industry-heading">Defense industry</h2>
            <span>Domestic capacity</span>
          </div>
          <p>{c.industry || 'No defense-industry analysis has been added yet.'}</p>
        </section>
        <section
          className="country-wiki-section country-wiki-section--wide"
          aria-labelledby="conflicts-heading"
        >
          <div className="wiki-section-head">
            <h2 id="conflicts-heading">Conflicts &amp; operations</h2>
            <span>{(c.history ?? []).length} entries</span>
          </div>
          {(c.history ?? []).length ? (
            <ol className="conflict-timeline">
              {c.history.map((entry, index) => {
                const year = entry.match(/\b(?:19|20)\d{2}(?:[–-](?:\d{2,4})?)?/);
                const detail = year
                  ? entry
                      .replace(year[0], '')
                      .replace(/^\s*[—-]\s*|\s*[—-]\s*$/g, '')
                      .trim()
                  : entry;
                return (
                  <li key={`${entry}-${index}`}>
                    <span>{year?.[0] || index + 1}</span>
                    <p>{detail}</p>
                  </li>
                );
              })}
            </ol>
          ) : (
            <p>No conflict history has been added yet.</p>
          )}
        </section>
        <section
          className="country-wiki-section country-wiki-section--wide"
          aria-labelledby="alliances-heading"
        >
          <div className="wiki-section-head">
            <h2 id="alliances-heading">Alliances &amp; partners</h2>
            <span>Strategic relationships</span>
          </div>
          <div className="alliance-tags">
            {(c.alliances ?? []).map((alliance) => (
              <span key={alliance}>{alliance}</span>
            ))}
          </div>
        </section>
      </div>
      <section
        className="country-wiki-section country-profile-section"
        aria-labelledby="forces-heading"
      >
        <div className="wiki-section-head">
          <h2 id="forces-heading">Armed forces</h2>
          <span>{Object.keys(c.forces ?? {}).length} services</span>
        </div>
        <div className="forces-grid">
          {Object.entries(c.forces ?? {}).map(([key, force]) => (
            <article className="force-card" key={key}>
              <h3>{force.name || humanize(key)}</h3>
              <p className="force-personnel">
                <span>Personnel</span>
                <strong>{force.personnel}</strong>
              </p>
              <ul>
                {Object.entries(force)
                  .filter(
                    ([field, value]) => !['name', 'personnel', 'note'].includes(field) && value,
                  )
                  .map(([field, value]) => (
                    <li key={field}>
                      <b>{humanize(field)}:</b> {value}
                    </li>
                  ))}
              </ul>
              {force.note && <p className="section-note">{force.note}</p>}
            </article>
          ))}
        </div>
      </section>
      <section
        className="country-wiki-section country-profile-section"
        aria-labelledby="ranks-heading"
      >
        <div className="wiki-section-head">
          <h2 id="ranks-heading">Rank system</h2>
          <span>English / Persian</span>
        </div>
        <div className="rank-grid">
          {(
            [
              ['ground_air_officers', 'Ground & Air officers'],
              ['navy_officers', 'Navy officers'],
              ['enlisted', 'Enlisted'],
            ] as const
          ).map(([key, title]) => (
            <div className="rank-table" key={key}>
              <h3>{title}</h3>
              <table>
                <thead>
                  <tr>
                    <th>Rank</th>
                    <th>Persian</th>
                  </tr>
                </thead>
                <tbody>
                  {(c.ranks?.[key] ?? []).map(([rank, persian]) => (
                    <tr key={`${rank}-${persian}`}>
                      <td>{rank}</td>
                      <td lang="fa">{persian}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
        {c.ranks?.note && <p className="section-note rank-note">{c.ranks.note}</p>}
      </section>
      <div className="country-toolbar">
        <div className="cat-search">
          <span aria-hidden>⌕</span>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search systems…"
            aria-label="Search systems"
          />
          {q && <button className="cat-clear" onClick={() => setQuery('')} aria-label="Clear search">✕</button>}
        </div>
        <div className="cat-chips" role="tablist" aria-label="Weapon categories">
          <button
            className={`cat-chip${activeCat === 'All' ? ' on' : ''}`}
            onClick={() => setActiveCat('All')}
            role="tab"
            aria-selected={activeCat === 'All'}
          >
            All <span>{weapons.data?.length ?? 0}</span>
          </button>
          {catOrder.map((c) => (
            <button
              key={c}
              className={`cat-chip${activeCat === c ? ' on' : ''}`}
              onClick={() => setActiveCat(c)}
              role="tab"
              aria-selected={activeCat === c}
            >
              {c} <span>{counts[c] ?? 0}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="weapon-categories">
        {catOrder
          .filter((category) => activeCat === 'All' || activeCat === category)
          .map((category) => (
          <section key={category} className="weapon-category">
            <div className="wiki-section-head">
              <div>
                <span className="eyebrow">Arsenal category</span>
                <h2>{category}</h2>
              </div>
              {user ? (
                <Button variant="secondary" onClick={() => setEditing(blank(category))}>
                  + Add system
                </Button>
              ) : (
                <span className="signin-hint">Sign in to edit</span>
              )}
            </div>
            {(() => {
              const items = (grouped[category] ?? []).filter(matches);
              if (items.length === 0) {
                return q ? (
                  <EmptyState title="No systems match your search" message="Try a different name or category." />
                ) : (
                  <EmptyState title="No systems in this category yet" message="Add the first verified system record." />
                );
              }
              return (
                <div className="weapon-grid">
                  {items.map((w) => (
                    <article
                      className={`weapon-card${selectedWeapon?.id === w.id ? ' selected' : ''}`}
                      key={w.id}
                      data-testid={`weapon-${w.id}`}
                      onClick={() => setSelectedWeapon(selectedWeapon?.id === w.id ? null : w)}
                    >
                      <header>
                        <div>
                          <h3>{w.name}</h3>
                          <p>{w.type}</p>
                        </div>
                        <span>V{w.version}</span>
                      </header>
                      <div className="weapon-stat-tiles" aria-label={`${w.name} estimates`}>
                        {(['stock', 'used', 'destroyed'] as const).map((key) => (
                          <div key={key}>
                            <span>{specLabels[key]}</span>
                            <strong className={w.specs[key] === 'unknown' ? 'unknown-value' : undefined}>
                              {w.specs[key] || '—'}
                            </strong>
                            <small>est.</small>
                          </div>
                        ))}
                      </div>
                      <table>
                        <tbody>
                          {cardSpecs(w.category, w.specs).map(([key, value]) => (
                            <tr key={key}>
                              <th>{specLabels[key] ?? humanize(key)}</th>
                              <td className={value === 'unknown' ? 'unknown-value' : undefined}>
                                {value === 'unknown' ? <em>unknown</em> : value}
                                {/est\.?/i.test(value) && (
                                  <small className="est-mark">est.</small>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {w.description && <p className="weapon-desc">{w.description}</p>}
                      {w.specs.combat_use && w.specs.combat_use !== 'unknown' && (
                        <p className="combat-use">⚔ {w.specs.combat_use}</p>
                      )}
                      {w.images && w.images.length > 0 && (
                        <div className="weapon-gallery" aria-label={`${w.name} gallery`}>
                          {w.images.map((img, idx) => (
                            <figure key={idx}>
                              <button
                                className="gallery-thumb"
                                onClick={() => setLightbox({ image: img, name: w.name })}
                                aria-label={`View ${w.name} image`}
                              >
                                <img src={img.url} alt={img.caption} loading="lazy" />
                              </button>
                              {img.caption && <figcaption>{img.caption}</figcaption>}
                            </figure>
                          ))}
                        </div>
                      )}
                      <footer>
                        <button onClick={(e) => { e.stopPropagation(); setHistory(w.id); }}>History</button>
                        {user ? (
                          <button onClick={(e) => { e.stopPropagation(); setEditing(w); }}>Edit</button>
                        ) : (
                          <span>Sign in to edit</span>
                        )}
                      </footer>
                    </article>
                  ))}
                </div>
              );
            })()}
          </section>
        ))}
      </div>
      </div>
      <aside className="country-aside">
        {c.hide_locations ? (
          <section className="restricted-notice">
            <span>🛡</span>
            <h3>Location data restricted</h3>
            <p>Per Guild rules (§8 / §109g), live locations of allied partners are never shown. Event positions and weapon target history are withheld for this country.</p>
          </section>
        ) : (
          <>
            <MapView
              center={[53.7, 32.4]}
              zoom={5.2}
              events={countryEvents}
              targets={selectedWeapon?.targets ?? []}
              onMapReady={(m) => { mapRef.current = m; }}
            />
            <section className="country-events">
              <header><span>Latest events</span><b>{countryEvents.length}</b></header>
              {countryEvents.length === 0 ? (
                <p className="rail-empty">No events in the current data window.</p>
              ) : (
                <ul>
                  {countryEvents
                    .slice()
                    .sort((a, b) => String(b.date).localeCompare(String(a.date)))
                    .slice(0, 6)
                    .map((e, i) => (
                      <li key={i}>
                        <button
                          onClick={() => {
                            const m = mapRef.current;
                            if (!m) return;
                            m.flyTo({ center: [e.lng, e.lat], zoom: 7 });
                          }}
                        >
                          <span className={`dot dot--${(e.fat ?? 0) > 0 ? 'red' : 'yellow'}`} />
                          <span className="ev-t">{e.title}</span>
                          <span className="ev-m">{e.date} · {e.admin1 || e.country}</span>
                        </button>
                      </li>
                    ))}
                </ul>
              )}
            </section>
            {selectedWeapon && (
              <section className="targets-panel">
                <header>
                  <span>Targets · {selectedWeapon.name}</span>
                  <button onClick={() => setSelectedWeapon(null)} aria-label="Clear weapon selection">✕</button>
                </header>
                {(selectedWeapon.targets ?? []).length === 0 ? (
                  <p className="rail-empty">No documented target locations for this system.</p>
                ) : (
                  <ul>
                    {(selectedWeapon.targets ?? []).map((t, i) => (
                      <li key={i}>
                        <span className="t-place">{t.place}</span>
                        <span className="t-meta">{t.date} · {t.note}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            )}
          </>
        )}
      </aside>
      </div>
      {editing && (
        <WeaponEditor
          weapon={editing}
          onClose={() => setEditing(null)}
          upload={actions.upload}
          onSave={(w) => actions.saveWeapon.mutateAsync(w).then(() => undefined)}
        />
      )}{' '}
      {editingCountry && (
        <CountryEditor
          country={c}
          onClose={() => setEditingCountry(false)}
          onSave={(draft) => actions.updateCountry.mutateAsync(draft).then(() => undefined)}
        />
      )}
      {history && <History id={history} onClose={() => setHistory(null)} />}
      {lightbox && (
        <div
          className="image-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={`${lightbox.name} image preview`}
          onClick={() => setLightbox(null)}
        >
          <button
            className="lightbox-close"
            aria-label="Close image"
            onClick={() => setLightbox(null)}
          >
            ×
          </button>
          <figure onClick={(event) => event.stopPropagation()}>
            <img src={lightbox.image.url} alt={lightbox.image.caption || lightbox.name} />
            <figcaption>{lightbox.image.caption || lightbox.name}</figcaption>
          </figure>
        </div>
      )}
    </div>
  );
}
